import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { KeyOutlined } from '@mui/icons-material';
import { SectionHeaderWithCommand, SectionPaper } from '../../../../common/components';
import { HstUserIdEditor, HstCenterEditor, HstRoleEditor, HstIdentityEditor } from '../fields/hstAccess';
import { useHstAccessEditor } from '../../../hooks/useHstAccessEditor';
import { HstProvisioningStatus } from './HstProvisioningStatus';
import { EditorMode } from '../../../types/editorTypes';
import { useFeatureFlag } from '../../../../../utils/featureFlags';

interface HstAccessSectionProps {
  mode?: EditorMode;
}

/**
 * HstAccessSection - Section for HST access request fields
 * Shown when ticket/recurring task category is "HST Access"
 */
export const HstAccessSection: React.FC<HstAccessSectionProps> = ({ mode = 'ticket' }) => {
  const title = mode === 'recurringTask' ? 'HST Access Request Template' : 'HST Access Request';
  const provisioningEnabled = useFeatureFlag('HstProvisioning');
  const { request, ticketStatus, ticketPersisted } = useHstAccessEditor(mode);

  return (
    <Box>
      <SectionHeaderWithCommand
        icon={<KeyOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title={title}
      />
      
      <SectionPaper>
        <Stack spacing={3}>
          {mode === 'ticket' && ticketPersisted && <HstProvisioningStatus request={request} ticketStatus={ticketStatus} />}

          {/* User Selection - Full Width */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 500, 
                color: '#64748b' 
              }}
            >
              Select User
            </Typography>
            <HstUserIdEditor mode={mode} />
          </Box>

          {provisioningEnabled && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
                HST Identity
              </Typography>
              <HstIdentityEditor mode={mode} />
            </Box>
          )}
          
          {/* Center and Role Selection - Side by Side */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                fontWeight: 500, 
                color: '#64748b' 
              }}
            >
              Access Details
            </Typography>
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <HstCenterEditor mode={mode} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <HstRoleEditor mode={mode} />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </SectionPaper>
    </Box>
  );
};

export default HstAccessSection;
